import {
	Button,
	ChipModal,
	ChipModalBody,
	ChipModalField,
	ChipModalFooter,
	ChipModalHeader,
	NativeSelect,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { useState } from "react";
import { Link, useParams } from "react-router";
import {
	Async,
	Badge,
	Empty,
	Json,
	KV,
	PageHead,
	Pager,
	SearchBox,
	Segmented,
	Status,
	useAction,
	useConfirm,
} from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { api, type Page, type PageDetail } from "@/lib/api";
import * as fmt from "@/lib/format";
import { useAsync, useDebounced, usePaged } from "@/lib/hooks";

const KINDS = ["entity", "vs", "alternatives", "best", "dataset", "report", "faq"];

export function PagesPage() {
	const t = useT();
	const { locale } = useI18n();
	const [q, setQ] = useState("");
	const [status, setStatus] = useState<"" | "review" | "draft" | "published" | "unpublished">("review");
	const [kind, setKind] = useState("");
	const query = useDebounced(q);
	const { state, rows, hasMore, loadingMore, more } = usePaged(
		(offset, limit) => api.pages({ q: query, status, kind, limit, offset }),
		(page) => page.pages,
		[query, status, kind],
	);

	return (
		<>
			<PageHead title={t("pages.title")} help={t("pages.lede")} />
			<div className="table-tools">
				<SearchBox value={q} onChange={setQ} />
				<Segmented
					value={status}
					onChange={setStatus}
					options={[
						{ value: "review", label: t("pages.status.review") },
						{ value: "draft", label: t("pages.status.draft") },
						{ value: "published", label: t("pages.status.published") },
						{ value: "unpublished", label: t("pages.status.unpublished") },
						{ value: "", label: t("state.all") },
					]}
				/>
				<NativeSelect aria-label={t("pages.kind")} value={kind} onChange={(e) => setKind(e.target.value)}>
					<option value="">{t("state.all")}</option>
					{KINDS.map((k) => (
						<option key={k} value={k}>
							{k}
						</option>
					))}
				</NativeSelect>
			</div>
			<Async state={state}>
				{({ counts }) => {
					// The counts are per status with no search applied, so with a
					// query the total is not known and the foot says only what is shown.
					const total = query
						? undefined
						: status
							? (counts.find((c) => c.status === status)?.count ?? rows.length)
							: counts.reduce((n, c) => n + c.count, 0);
					return rows.length === 0 ? (
						<Empty />
					) : (
						<>
							<div className="table-scroll">
								<Table className="text-[length:var(--t-sm)]">
									<TableHeader>
										<TableRow>
											<TableHead>{t("pages.page")}</TableHead>
											<TableHead>{t("pages.kind")}</TableHead>
											<TableHead>{t("state.status")}</TableHead>
											<TableHead>{t("pages.owner")}</TableHead>
											<TableHead>{t("pages.about")}</TableHead>
											<TableHead>{t("state.updated")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{rows.map((p: Page) => (
											<TableRow key={p.id}>
												<TableCell>
													<Link className="cell-title" to={`/pages/${p.id}`}>
														{p.title}
													</Link>
													<span className="sub mono cell-summary" title={p.slug}>
														{p.slug} · {p.locale} · r{p.revision}
													</span>
												</TableCell>
												<TableCell>{p.kind}</TableCell>
												<TableCell>
													<div className="chips">
														<Status value={p.status} prefix="pages.status" />
													</div>
												</TableCell>
												<TableCell>
													{p.owner_org_id ? (
														<Link to={`/orgs/${p.owner_org_id}`}>{p.owner_name ?? p.owner_org_id}</Link>
													) : (
														"—"
													)}
												</TableCell>
												<TableCell className="small">{p.entity_names.join(", ")}</TableCell>
												<TableCell className="whitespace-nowrap">{fmt.date(locale, p.updated_at)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
							<Pager shown={rows.length} total={total} hasMore={hasMore} busy={loadingMore} onMore={more} />
						</>
					);
				}}
			</Async>
		</>
	);
}

export function PageDetailPage() {
	const { id = "" } = useParams();
	const t = useT();
	const { locale } = useI18n();
	const state = useAsync<PageDetail>(() => api.page(id), [id]);
	const confirm = useConfirm();
	const { run, busy } = useAction();
	const [reason, setReason] = useState("");
	const [taking, setTaking] = useState(false);

	return (
		<Async state={state}>
			{(d) => {
				const p = d.page;
				return (
					<>
						<PageHead
							title={p.title}
							lede={
								<span className="chips">
									<Status value={p.status} prefix="pages.status" />
									<Badge tone="outline">{p.kind}</Badge>
									<Badge tone="outline">{p.locale}</Badge>
								</span>
							}
							crumbs={[{ to: "/pages", label: t("pages.title") }]}
						>
							<Button asChild variant="default">
								<a href={d.url} target="_blank" rel="noreferrer">
									{t("pages.openPublic")}
								</a>
							</Button>
							{p.status !== "published" ? (
								<Button
									variant="primary"
									type="button"
									disabled={busy}
									onClick={async () => {
										const ok = await confirm(
											t("pages.publish"),
											`${t("pages.publishConfirm", { org: p.owner_name ?? "—" })} ${t("pages.publishHint")}`,
										);
										if (ok) {
											const r = await run(
												() => api.publishPage(p.id),
												() => t("pages.published"),
											);
											if (r) state.reload();
										}
									}}
								>
									{t("pages.publish")}
								</Button>
							) : (
								<Button variant="destructive" type="button" disabled={busy} onClick={() => setTaking(true)}>
									{t("pages.unpublish")}
								</Button>
							)}
						</PageHead>

						<div className="grid-2 detail-grid">
							<div className="card">
								<KV
									items={[
										[t("state.id"), <span className="mono">{p.id}</span>],
										[
											"URL",
											<a className="detail-url mono" href={d.url} target="_blank" rel="noreferrer">
												{fmt.readableUrl(d.url)}
											</a>,
										],
										[
											t("pages.owner"),
											p.owner_org_id ? (
												<Link to={`/orgs/${p.owner_org_id}`}>{p.owner_name ?? p.owner_org_id}</Link>
											) : (
												"—"
											),
										],
										[t("pages.about"), p.entity_names.join(", ")],
										[t("pages.recipe"), p.recipe],
										[t("pages.generatedBy"), p.generated_by],
										[t("pages.revision"), p.revision],
										[t("entities.published"), fmt.dateTime(locale, p.published_at)],
										[t("state.updated"), fmt.dateTime(locale, p.updated_at)],
										["", t("pages.citations", { n: d.citations })],
									]}
								/>
								{d.description ? <p style={{ marginTop: "var(--s-6)" }}>{d.description}</p> : null}
							</div>
							<div className="card">
								<h2>{t("pages.reviewState")}</h2>
								<Json value={d.review_state} />
							</div>
							<div className="data-section data-section-wide">
								<h2>{t("pages.revisions")}</h2>
								{d.revisions.length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<Table className="text-[length:var(--t-sm)]">
										<TableBody>
											{d.revisions.map((r) => (
												<TableRow key={r.revision}>
													<TableCell className="num text-right whitespace-nowrap">r{r.revision}</TableCell>
													<TableCell>
														<span className="mono cell-summary" title={r.author ?? undefined}>
															{r.author ?? "—"}
														</span>
													</TableCell>
													<TableCell className="small">{r.note ?? ""}</TableCell>
													<TableCell className="whitespace-nowrap">{fmt.dateTime(locale, r.created_at)}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</div>
						</div>
						<div className="section">
							<h2>{t("pages.blocks")}</h2>
							<Json value={d.blocks} />
						</div>
						<div className="section">
							<h2>{t("pages.jsonld")}</h2>
							<Json value={d.jsonld} />
						</div>

						{/* The reason used to be an input parked in the page head, live on a
						    page nobody was taking down. It belongs with the decision. */}
						{taking ? (
							<ChipModal
								open
								dismissDisabled={busy}
								srTitle={t("pages.unpublish")}
								onOpenChange={() => setTaking(false)}
							>
								<ChipModalHeader onClose={() => setTaking(false)}>{t("pages.unpublish")}</ChipModalHeader>
								<ChipModalBody>
									<fieldset className="form" disabled={busy}>
										<ChipModalField
											type="input"
											title={t("pages.unpublishReason")}
											hint={t("pages.unpublishHint")}
											value={reason}
											onChange={setReason}
										/>
									</fieldset>
								</ChipModalBody>
								<ChipModalFooter
									cancelLabel={t("state.cancel")}
									onCancel={() => setTaking(false)}
									defaultAction="none"
									primaryAction={{
										label: t("pages.unpublish"),
										variant: "destructive",
										disabled: busy,
										onClick: () =>
											void run(
												() => api.unpublishPage(p.id, reason || undefined),
												() => t("pages.unpublished"),
											).then((r) => {
												if (r) {
													setTaking(false);
													state.reload();
												}
											}),
									}}
								/>
							</ChipModal>
						) : null}
					</>
				);
			}}
		</Async>
	);
}
